import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';

const SyncMonitor = ({ devices, syncStatus }) => {
  const getSyncStatusInfo = (deviceId) => {
    const status = syncStatus?.[deviceId];
    if (status === 'syncing') {
      return { color: 'text-warning', bg: 'bg-warning/10', icon: 'RefreshCw', label: 'Syncing' };
    }
    if (status === 'success') {
      return { color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle', label: 'Success' };
    }
    if (status === 'error') {
      return { color: 'text-error', bg: 'bg-error/10', icon: 'AlertCircle', label: 'Error' };
    }
    return { color: 'text-muted-foreground', bg: 'bg-muted/30', icon: 'Clock', label: 'Idle' };
  };

  const getConnectionHealth = () => {
    const total = devices?.length;
    const errors = Object.values(syncStatus)?.filter(s => s === 'error')?.length;
    if (errors === 0) return { label: 'Excellent', color: 'text-success', percentage: 100 };
    if (errors < total * 0.3) return { label: 'Good', color: 'text-warning', percentage: 70 };
    return { label: 'Poor', color: 'text-error', percentage: 40 };
  };

  const health = getConnectionHealth();

  return (
    <div className="bg-card rounded-xl p-6 elevation-2">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-warning/10 rounded-lg">
            <Icon name="Monitor" size={24} className="text-warning" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-foreground">Sync Monitor</h2>
            <p className="text-sm text-muted-foreground">Connection health and sync status</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">Health:</span>
          <span className={`text-sm font-semibold ${health?.color}`}>{health?.label}</span>
        </div>
      </div>
      {/* Connection Health Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">Overall Connection Health</span>
          <span className={`text-sm font-semibold ${health?.color}`}>{health?.percentage}%</span>
        </div>
        <div className="h-3 bg-muted rounded-full overflow-hidden">
          <motion.div
            className={`h-full ${health?.color?.replace('text-', 'bg-')}`}
            initial={{ width: 0 }}
            animate={{ width: `${health?.percentage}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
          />
        </div>
      </div>
      {/* Device Sync Status List */}
      <div className="space-y-3">
        {devices?.map((device) => {
          const statusInfo = getSyncStatusInfo(device?.id);

          return (
            <motion.div
              key={device?.id}
              className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-wellness-light"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center space-x-4 flex-1">
                <div className={`p-2 rounded-lg ${statusInfo?.bg}`}>
                  <Icon name={statusInfo?.icon} size={18} className={statusInfo?.color} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{device?.deviceName}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {device?.deviceType?.replace('_', ' ')}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Status</p>
                  <p className={`text-sm font-medium ${statusInfo?.color}`}>{statusInfo?.label}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Last Sync</p>
                  <p className="text-sm font-medium text-foreground">
                    {device?.lastSyncAt ? format(new Date(device?.lastSyncAt), 'HH:mm:ss') : 'Never'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Latency</p>
                  <p className="text-sm font-medium text-foreground">
                    {Math.floor(Math.random() * 500) + 100}ms
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      {/* Sync Statistics */}
      <div className="grid grid-cols-3 gap-4 mt-6">
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-xs text-muted-foreground">Successful Syncs</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {Object.values(syncStatus)?.filter(s => s === 'success')?.length}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="RefreshCw" size={16} className="text-warning" />
            <span className="text-xs text-muted-foreground">In Progress</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {Object.values(syncStatus)?.filter(s => s === 'syncing')?.length}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="AlertCircle" size={16} className="text-error" />
            <span className="text-xs text-muted-foreground">Errors</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {Object.values(syncStatus)?.filter(s => s === 'error')?.length}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SyncMonitor;
